import type { DescuentoDeLinea, DescuentoDeVenta, ImpuestoDeProducto, LineaMostrador, Producto, TotalesVentas } from '@tanteo/compartido';
import { ErrorDeApi } from './error-api';
import { normalizarSku, sumarVentas } from '@tanteo/compartido';
import { supabase } from './supabase';

/** Columnas del impuesto que hay que traer para poder descartar los que ya no se cobran. */
export const EMBEBIDO_IMPUESTOS =
  'producto_impuestos ( impuestos_negocio ( id, impuesto_nombre, impuesto_tipo, impuesto_tarifa, impuesto_activo, eliminado_en, impuesto_descuento_reduce_base ) )';

/**
 * El embebido de PostgREST llega anidado; se normaliza aquí y no en cada pantalla. Descarta
 * inactivos y borrados con el criterio de `registrar_venta`, o el mostrador cobraría de más.
 */
export function impuestosDe(filas: unknown): ImpuestoDeProducto[] {
  // Los nombres son los de la tabla, que es lo que devuelve el embebido de arriba: uno que el
  // select no pida vale `undefined` y el filtro descartaría el impuesto sin decir nada.
  type FilaImpuesto = {
    impuestos_negocio:
      | {
          id: string;
          impuesto_nombre: string;
          impuesto_tipo: 'iva' | 'inc';
          impuesto_tarifa: number;
          impuesto_activo: boolean;
          eliminado_en: string | null;
          impuesto_descuento_reduce_base: boolean;
        }
      | null;
  };
  return ((filas ?? []) as FilaImpuesto[])
    .map((r) => r.impuestos_negocio)
    .filter((i): i is NonNullable<typeof i> => i !== null && i.impuesto_activo && i.eliminado_en === null)
    .map((i) => ({
      id: i.id,
      nombre: i.impuesto_nombre,
      tipo: i.impuesto_tipo,
      tarifa: Number(i.impuesto_tarifa),
      reduceBase: i.impuesto_descuento_reduce_base,
    }));
}

export async function listarProductos(negocioId: string): Promise<Producto[]> {
  const { data, error, status } = await supabase
    .from('productos')
    .select(`id, sku, ean, nombre, categoria, precio, es_activo, ${EMBEBIDO_IMPUESTOS}`)
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('categoria')
    // El SKU, único por negocio, es el orden del catálogo: el mismo venga de donde venga el producto.
    .order('sku');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((p) => ({
    id: p.id,
    sku: p.sku,
    ean: p.ean,
    nombre: p.nombre,
    categoria: p.categoria,
    precio: Number(p.precio),
    impuestos: impuestosDe(p.producto_impuestos),
    activo: p.es_activo,
  }));
}

/** Índice de búsqueda por código: SKU normalizado y EAN apuntan al producto. */
export function indexarProductos(productos: Producto[]): Map<string, Producto> {
  const indice = new Map<string, Producto>();
  for (const p of productos) {
    if (!p.activo) continue;
    indice.set(normalizarSku(p.sku), p);
    if (p.ean) indice.set(p.ean.trim(), p);
  }
  return indice;
}

export interface ResultadoVenta {
  ventaId: string;
  consecutivo: string;
  total: number;
}

/** Lo que `registrar_venta` recibe de un descuento: su id, y la cifra solo si es el libre. */
function descuentoParaServidor(d: DescuentoDeLinea | DescuentoDeVenta) {
  return d.libre ? { descuento_id: d.descuentoId, tipo: d.tipo, valor: d.valor } : { descuento_id: d.descuentoId };
}

export async function registrarVentaRpc(params: {
  negocioId: string;
  uuidCliente: string;
  cuentaId: string | null;
  /** Reparto por cuenta cuando el cobro es mixto; sin él, toda la venta va a `cuentaId`. */
  pagos?: PagoDeVenta[] | null;
  terceroId: string | null;
  lineas: LineaMostrador[];
  descuento?: DescuentoDeVenta | null;
  pedidoNumero?: number | null;
  fecha?: string;
  hora?: string;
}): Promise<ResultadoVenta> {
  // Los descuentos viajan solo cuando hay: una venta sin ellos llama igual que siempre.
  const { data, error, status } = await supabase.rpc('registrar_venta', {
    p_negocio: params.negocioId,
    p_uuid_cliente: params.uuidCliente,
    p_metodo_pago: null,
    p_tercero: params.terceroId,
    p_lineas: params.lineas.map((l) => ({
      producto_id: l.productoId,
      cantidad: l.cantidad,
      ...(l.descuento ? { descuento: descuentoParaServidor(l.descuento) } : {}),
      // Los automáticos que el cajero quitó con la ✕: la base los deja en la auditoría.
      ...(l.promocionQuitada?.length ? { automaticos_quitados: l.promocionQuitada } : {}),
    })),
    p_fecha: params.fecha ?? null,
    p_hora: params.hora ?? null,
    p_cuenta: params.cuentaId,
    p_pagos: params.pagos?.length ? params.pagos.map((g) => ({ cuenta_id: g.cuentaId, monto: g.monto })) : null,
    ...(params.descuento ? { p_descuento: descuentoParaServidor(params.descuento) } : {}),
    ...(params.pedidoNumero ? { p_pedido_numero: params.pedidoNumero } : {}),
  });
  if (error) throw new ErrorDeApi(error, status);
  const fila = Array.isArray(data) ? data[0] : data;
  // Un 200 sin fila (un proxy que cortó el cuerpo) no es una venta registrada: se reintenta, y la base,
  // idempotente por `uuid_cliente`, devuelve la misma.
  if (!fila?.venta_id) throw new ErrorDeApi({ message: 'La venta no devolvió su comprobante' }, 502);
  return { ventaId: fila.venta_id, consecutivo: fila.consecutivo, total: Number(fila.total) };
}

/**
 * El precio de hoy de unos productos, para cobrar con lo vigente (A5). Tiene tope corto: sin
 * respuesta, quien llama cobra con el precio que tenía cargado.
 */
export async function preciosVigentes(negocioId: string, ids: string[]): Promise<Map<string, number>> {
  const { data, error, status } = await supabase
    .from('productos')
    .select('id, precio')
    .eq('negocio_id', negocioId)
    .in('id', ids)
    .abortSignal(AbortSignal.timeout(2500));
  if (error) throw new ErrorDeApi(error, status);
  return new Map((data ?? []).map((p) => [p.id as string, Number(p.precio)]));
}

export async function anularVentaRpc(ventaId: string, motivo: string): Promise<void> {
  const { error, status } = await supabase.rpc('anular_venta', { p_venta: ventaId, p_motivo: motivo });
  if (error) throw new ErrorDeApi(error, status);
}

export interface VentaResumen {
  id: string;
  consecutivo: string;
  fecha: string;
  hora: string;
  cuenta: string;
  /** Cómo entró la plata. Con un solo pago es la cuenta de siempre; con varios, el cobro fue mixto. */
  pagos: { cuenta: string; monto: number }[];
  total: number;
  estado: 'emitida' | 'anulada';
  motivoAnulacion: string | null;
}

export async function listarVentas(negocioId: string, fecha?: string): Promise<VentaResumen[]> {
  let q = supabase
    .from('comprobantes')
    .select(
      'id, consecutivo, fecha, hora, total, estado, anulada_motivo, cuentas_negocio:cuenta_id ( cuenta_nombre ), comprobante_pagos ( pago_monto, pago_orden, cuentas_negocio:cuenta_id ( cuenta_nombre ) )'
    )
    .eq('negocio_id', negocioId)
    .order('consecutivo', { ascending: false })
    .limit(200);
  if (fecha) q = q.eq('fecha', fecha);
  const { data, error, status } = await q;
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((v) => ({
    id: v.id,
    consecutivo: v.consecutivo,
    fecha: v.fecha,
    hora: String(v.hora).slice(0, 5),
    cuenta: (v.cuentas_negocio as unknown as { cuenta_nombre: string } | null)?.cuenta_nombre ?? 'Sin cuenta',
    pagos: ((v.comprobante_pagos ?? []) as unknown as { pago_monto: number; pago_orden: number; cuentas_negocio: { cuenta_nombre: string } | null }[])
      .slice()
      .sort((a, b) => a.pago_orden - b.pago_orden)
      .map((g) => ({ cuenta: g.cuentas_negocio?.cuenta_nombre ?? 'Sin cuenta', monto: Number(g.pago_monto) })),
    total: Number(v.total),
    estado: v.estado,
    motivoAnulacion: v.anulada_motivo,
  }));
}

export interface VentaDetalle extends VentaResumen {
  subtotal: number;
  impuesto: number;
  /** Rebajas de producto y a la venta; `subtotal` ya es neto de ambas. */
  descuento: number;
  descuentoLineas: number;
  descuentoVenta: { tipo: 'pct' | 'monto'; valor: number; nombre: string | null } | null;
  /** El pedido del que salió la venta; null en una venta suelta. */
  pedidoNumero: number | null;
  cajero: string;
  cliente: { nombre: string; numeroDocumento: string; correo: string | null } | null;
  /** `totalLinea` es el bruto; `rebaja` es la del producto, que el impreso muestra debajo de la línea. */
  lineas: {
    linea: number;
    nombre: string;
    cantidad: number;
    precioUnit: number;
    totalLinea: number;
    rebaja: { nombre: string | null; tipo: string; valor: number | null; monto: number } | null;
  }[];
}

export async function obtenerVenta(negocioId: string, ventaId: string): Promise<VentaDetalle> {
  const { data: v, error, status } = await supabase
    .from('comprobantes')
    .select(
      // El nombre del cajero está congelado en el comprobante: por join, renombrarse cambiaría el
      // que ya salió impreso en comprobantes emitidos.
      'id, consecutivo, fecha, hora, subtotal, impuesto, total, estado, anulada_motivo, cajero_nombre_congelado, descuento, descuento_lineas, descuento_tipo, descuento_valor, descuento_motivo, pedido_numero, terceros:tercero_id ( nombre, documento_numero, correo ), cuentas_negocio:cuenta_id ( cuenta_nombre ), comprobante_pagos ( pago_monto, pago_orden, cuentas_negocio:cuenta_id ( cuenta_nombre ) )'
    )
    .eq('negocio_id', negocioId)
    .eq('id', ventaId)
    .single();
  if (error) throw new ErrorDeApi(error, status);
  const { data: lineas, error: e2, status: statusLineas } = await supabase
    .from('comprobante_lineas')
    .select(
      'linea_orden, producto_nombre_congelado, linea_cantidad, producto_precio_congelado, linea_total, linea_descuento, comprobante_linea_descuentos ( descuento_origen, descuento_tipo, descuento_valor, descuento_nombre_congelado, descuento_monto )'
    )
    .eq('comprobante_id', ventaId)
    .order('linea_orden');
  if (e2) throw new ErrorDeApi(e2, statusLineas);
  const cajero = (v.cajero_nombre_congelado as string) ?? '';
  const t = v.terceros as unknown as { nombre: string; documento_numero: string; correo: string | null } | null;
  return {
    id: v.id,
    consecutivo: v.consecutivo,
    fecha: v.fecha,
    hora: String(v.hora).slice(0, 5),
    cuenta: (v.cuentas_negocio as unknown as { cuenta_nombre: string } | null)?.cuenta_nombre ?? 'Sin cuenta',
    subtotal: Number(v.subtotal),
    impuesto: Number(v.impuesto),
    descuento: Number(v.descuento ?? 0),
    descuentoLineas: Number(v.descuento_lineas ?? 0),
    descuentoVenta: v.descuento_tipo
      ? { tipo: v.descuento_tipo, valor: Number(v.descuento_valor), nombre: v.descuento_motivo ?? null }
      : null,
    pedidoNumero: v.pedido_numero ?? null,
    total: Number(v.total),
    estado: v.estado,
    motivoAnulacion: v.anulada_motivo,
    cajero,
    pagos: ((v.comprobante_pagos ?? []) as unknown as { pago_monto: number; pago_orden: number; cuentas_negocio: { cuenta_nombre: string } | null }[])
      .slice()
      .sort((a, b) => a.pago_orden - b.pago_orden)
      .map((g) => ({ cuenta: g.cuentas_negocio?.cuenta_nombre ?? 'Sin cuenta', monto: Number(g.pago_monto) })),
    cliente: t ? { nombre: t.nombre, numeroDocumento: t.documento_numero, correo: t.correo ?? null } : null,
    lineas: (lineas ?? []).map((i) => {
      type Rebaja = {
        descuento_origen: string;
        descuento_tipo: string;
        descuento_valor: number | null;
        descuento_nombre_congelado: string | null;
        descuento_monto: number;
      };
      const propia = ((i.comprobante_linea_descuentos ?? []) as Rebaja[]).find((d) => d.descuento_origen !== 'venta');
      return {
        linea: i.linea_orden,
        nombre: i.producto_nombre_congelado,
        cantidad: Number(i.linea_cantidad),
        precioUnit: Number(i.producto_precio_congelado),
        totalLinea: Number(i.linea_total) + Number(i.linea_descuento ?? 0),
        rebaja: propia
          ? {
              nombre: propia.descuento_nombre_congelado,
              tipo: propia.descuento_tipo,
              valor: propia.descuento_valor === null ? null : Number(propia.descuento_valor),
              monto: Number(propia.descuento_monto),
            }
          : null,
      };
    }),
  };
}

export interface ResumenCuenta {
  cuentaId: string | null;
  cuenta: string;
  esEfectivo: boolean;
  /** Pagos, no ventas: una venta mixta deja un pago en cada cuenta. */
  numPagos: number;
  total: number;
  numAnulados: number;
  totalAnulado: number;
}

/** Un pago de una venta: la cuenta por donde entró y cuánto. */
export interface PagoDeVenta {
  cuentaId: string;
  monto: number;
}

/**
 * Los avisos de operación del negocio: qué hay que tocar, desde cuándo (nulo donde no aplica) y
 * cuántas veces se reportó. El texto viene del servidor: lo comparten pantalla, bot y Panel.
 */
export interface AvisoOperacion {
  tipo: string;
  pendientes: number;
  antiguedadDias: number | null;
  avisos: number;
  titulo: string;
  queHacer: string;
  /** Lo que se cuenta, en singular: la base la manda para que la pantalla no invente el nombre. */
  unidad: string;
}

export async function avisosDelNegocio(negocioId: string): Promise<AvisoOperacion[]> {
  const { data, error, status } = await supabase.rpc('avisos_del_negocio', { p_negocio: negocioId });
  if (error) throw new ErrorDeApi(error, status);
  type Fila = {
    tipo: string;
    pendientes: number;
    antiguedad_dias: number | null;
    avisos: number;
    titulo: string;
    que_hacer: string;
    unidad: string;
  };
  return ((data ?? []) as Fila[]).map((r) => ({
    tipo: r.tipo,
    pendientes: r.pendientes,
    antiguedadDias: r.antiguedad_dias,
    avisos: r.avisos,
    titulo: r.titulo,
    queHacer: r.que_hacer,
    unidad: r.unidad,
  }));
}

/**
 * El día por cuenta y consolidado: el cierre usa el detalle y Hoy el total. Los gastos van en su
 * propio mapa: una cuenta que pagó un gasto y no vendió no tiene fila de ventas.
 */
export async function resumenDia(
  negocioId: string,
  fecha: string
): Promise<{
  cuentas: ResumenCuenta[];
  totales: TotalesVentas;
  gastosCajaGeneral: number;
  gastosPorCuenta: Record<string, number>;
}> {
  const [dia, porCuenta, gastos] = await Promise.all([
    supabase
      .from('v_ventas_diarias')
      .select('num_ventas, total, num_anuladas, total_anulado')
      .eq('negocio_id', negocioId)
      .eq('fecha', fecha),
    supabase
      .from('v_dinero_por_cuenta')
      .select('cuenta_id, cuenta, es_efectivo, num_pagos, total, num_anulados, total_anulado')
      .eq('negocio_id', negocioId)
      .eq('fecha', fecha),
    supabase
      .from('v_gastos_por_cuenta')
      // La vista conserva `caja_general` como nombre de salida: es su contrato, y la columna de la
      // tabla que hay debajo se llama `es_caja_general`.
      .select('cuenta_id, caja_general, total')
      .eq('negocio_id', negocioId)
      .eq('fecha', fecha),
  ]);
  if (dia.error) throw new ErrorDeApi(dia.error, dia.status);
  if (porCuenta.error) throw new ErrorDeApi(porCuenta.error, porCuenta.status);
  if (gastos.error) throw new ErrorDeApi(gastos.error, gastos.status);
  const gastosPorCuenta: Record<string, number> = {};
  let gastosCajaGeneral = 0;
  for (const g of gastos.data ?? []) {
    if (g.caja_general) gastosCajaGeneral += Number(g.total);
    else if (g.cuenta_id) gastosPorCuenta[g.cuenta_id] = (gastosPorCuenta[g.cuenta_id] ?? 0) + Number(g.total);
  }
  return {
    gastosCajaGeneral,
    gastosPorCuenta,
    cuentas: (porCuenta.data ?? []).map((r) => ({
      cuentaId: r.cuenta_id,
      cuenta: r.cuenta ?? 'Sin cuenta',
      esEfectivo: r.es_efectivo === true,
      numPagos: Number(r.num_pagos),
      total: Number(r.total),
      numAnulados: Number(r.num_anulados),
      totalAnulado: Number(r.total_anulado),
    })),
    totales: sumarVentas(dia.data ?? []),
  };
}

/** Un movimiento de plata entre dos sitios: una cuenta del negocio o `caja-general`. */
export interface Traslado {
  de: string;
  a: string;
  monto: number;
}

interface CuadreGuardado {
  id: string;
  fecha: string;
  traslados: Traslado[];
  conteo: Record<string, number>;
  saldosIniciales: Record<string, number>;
  saldosFinales: Record<string, number>;
  efectivoContado: number;
  efectivoEsperado: number;
  diferencia: number;
  estado: 'abierto' | 'cerrado';
  abiertoEn: string | null;
}

export async function obtenerCuadre(negocioId: string, fecha: string): Promise<CuadreGuardado | null> {
  const { data, error, status } = await supabase
    .from('caja_cuadres')
    .select('id, fecha, cuenta_traslados, efectivo_conteo, cuenta_saldo_inicial, cuenta_saldo_final, efectivo_contado, efectivo_esperado, efectivo_diferencia, estado, abierto_en')
    .eq('negocio_id', negocioId)
    .eq('fecha', fecha)
    .maybeSingle();
  if (error) throw new ErrorDeApi(error, status);
  if (!data) return null;
  return {
    id: data.id,
    fecha: data.fecha,
    traslados: ((data.cuenta_traslados ?? []) as Traslado[]).map((t) => ({ de: t.de, a: t.a, monto: Number(t.monto) })),
    conteo: (data.efectivo_conteo ?? {}) as Record<string, number>,
    saldosIniciales: (data.cuenta_saldo_inicial ?? {}) as Record<string, number>,
    saldosFinales: (data.cuenta_saldo_final ?? {}) as Record<string, number>,
    efectivoContado: Number(data.efectivo_contado),
    efectivoEsperado: Number(data.efectivo_esperado),
    diferencia: Number(data.efectivo_diferencia),
    estado: data.estado,
    abiertoEn: data.abierto_en,
  };
}

/**
 * Deja constancia de quien abre el dia y con cuanto hay en cada cuenta. Un día que ya estaba abierto
 * no cambia: `yaEstaba` lo dice.
 */
export async function abrirCajaRpc(negocioId: string, fecha: string, saldos: Record<string, number>): Promise<{ yaEstaba: boolean }> {
  const { data, error, status } = await supabase.rpc('abrir_caja', { p_negocio: negocioId, p_fecha: fecha, p_saldos: saldos });
  if (error) throw new ErrorDeApi(error, status);
  return { yaEstaba: (data as { ya_estaba?: boolean } | null)?.ya_estaba === true };
}

/** Corrige la base de efectivo de un día abierto; el motivo queda en el historial. */
export async function corregirBaseCajaRpc(negocioId: string, fecha: string, valor: number, motivo: string): Promise<void> {
  const { error, status } = await supabase.rpc('corregir_base_caja', {
    p_negocio: negocioId,
    p_fecha: fecha,
    p_valor: valor,
    p_motivo: motivo,
  });
  if (error) throw new ErrorDeApi(error, status);
}

/**
 * Devuelve un día cerrado a abierto para poder cambiarlo. Exige el motivo y queda registrado: un
 * cierre no se deshace en silencio.
 */
export async function reabrirCuadreRpc(negocioId: string, fecha: string, motivo: string): Promise<void> {
  const { error, status } = await supabase.rpc('reabrir_cuadre', {
    p_negocio: negocioId,
    p_fecha: fecha,
    p_motivo: motivo,
  });
  if (error) throw new ErrorDeApi(error, status);
}

export async function guardarCuadreRpc(params: {
  negocioId: string;
  fecha: string;
  traslados: Traslado[];
  conteo: Record<string, number>;
  saldosFinales: Record<string, number>;
  cerrar: boolean;
}): Promise<void> {
  const { error, status } = await supabase.rpc('guardar_cuadre', {
    p_negocio: params.negocioId,
    p_fecha: params.fecha,
    p_traslados: params.traslados,
    p_conteo: params.conteo,
    p_saldos_finales: params.saldosFinales,
    p_cerrar: params.cerrar,
  });
  if (error) throw new ErrorDeApi(error, status);
}

export interface ProductoVendido {
  sku: string;
  nombre: string;
  unidades: number;
  total: number;
}

/**
 * Los que más se vendieron en el mes, para Reportes. Lee el nombre y el SKU congelados: un producto
 * renombrado o retirado sigue apareciendo con el nombre con el que se vendió.
 */
export async function masVendidosDelMes(negocioId: string, anio: number, mes: number): Promise<ProductoVendido[]> {
  const primeroDelMes = `${anio}-${String(mes).padStart(2, '0')}-01`;
  const { data, error, status } = await supabase
    .from('v_productos_mas_vendidos')
    .select('sku, nombre, unidades, total')
    .eq('negocio_id', negocioId)
    .eq('mes', primeroDelMes)
    .order('unidades', { ascending: false })
    .limit(20);
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((p) => ({
    sku: p.sku,
    nombre: p.nombre,
    unidades: Number(p.unidades),
    total: Number(p.total),
  }));
}
